import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { LocationQuery } from "vue-router";
import router from "@/router/router.ts";
import i18n from "@/shared/i18n/i18n";
import { APIError } from "@/shared/errors";
import { useSessionStore } from "@/features/auth/stores/session.ts";
import { storeToRefs } from "pinia";

// Flag to track ongoing token refresh
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Create Axios instance
const axiosInstance = axios.create({
  baseURL: import.meta.env.FARM_API_URL,
  timeout: import.meta.env.FARM_API_MAX_DEADLINE,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Api-Key": import.meta.env.FARM_API_KEY,
  },
});

// Request interceptor: Adds Authorization token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const sessionStore = useSessionStore();
    const { idToken } = storeToRefs(sessionStore);

    if (idToken.value) {
      let token = idToken.value;
      if (token.startsWith('"') && token.endsWith('"')) {
        token = token.slice(1, -1); // Remove quotes safely
      }
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error: AxiosError): Promise<APIError> => {
    return Promise.reject({
      code: "REQUEST_ERROR",
      message: i18n.global.t("common.genericError") || error.message,
      details: error.message || "",
    } as APIError);
  }
);

// Response interceptor: Handles 401, refresh logic
axiosInstance.interceptors.response.use(
  <T>(response: AxiosResponse<T>): T => {
    return response.data as T;
  },
  async (error: AxiosError) => {
    const sessionStore = useSessionStore();
    const { refreshToken } = storeToRefs(sessionStore);

    if (error.response?.status === 401) {
      let query: LocationQuery = {};

      if (router.currentRoute.value.name !== "Login") {
        query["redirect"] = router.currentRoute.value.fullPath;
        console.log("Redirecting to login with query", query);
      } else {
        query = router.currentRoute.value.query;
        console.log("Refreshing token with query", query);
      }

      if (!refreshToken.value) {
        sessionStore.clearSession();
        await router.push({name: "Login", query});
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await sessionStore.fetchRefreshSession();
          onRefreshed(newToken);
          isRefreshing = false;
        } catch (refreshError) {
          sessionStore.clearSession();
          await router.push({name: "Login", query});
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve) => {
        refreshSubscribers.push((token) => {
          if (!error.config) {
            return;
          }

          error.config.headers.Authorization = `Bearer ${token}`;
          resolve(axiosInstance(error.config));
        });
      });
    }

    let errorRes: APIError;
    if (error.response) {
      errorRes = error.response.data as APIError;
      errorRes.message = i18n.global.t(`errors.${errorRes.code}`) || errorRes.message;
    } else {
      errorRes = {
        name: "", stack: "",
        code: "network_error",
        message: i18n.global.t("errors.network_error") || error.message,
        details: error.message || ""
      };
    }

    return Promise.reject(errorRes);
  }
);

export { axiosInstance as Axios };
